import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'
import useEvent from 'react-use-event-hook'
import { zodResolver } from '@hookform/resolvers/zod'

import { CheckIcon } from '@gto/icons-react/outline/check'
import { ChevronDownIcon } from '@gto/icons-react/outline/chevron-down'
import { TrashIcon } from '@gto/icons-react/outline/trash'
import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { Menu } from '@gto/ui/menu'
import { Typography } from '@gto/ui/typography'

import { ConfirmDialog } from '@/components/confirm-dialog'
import { fieldErrorText, TextField } from '@/components/form'
import { FieldShell } from '@/components/form/field-shell'
import { MutationError } from '@/components/mutation-error'
import {
  type AffiliateCouponTagSchema,
  type AffiliateCreateCouponTagRequest,
  couponTagCreateFormSchema,
} from '@/domain/coupons'
import { extractValidationFieldErrors } from '@/utils/extract-validation-detail'

import {
  couponTagColorOptions,
  DEFAULT_COUPON_TAG_COLOR,
  getTagForegroundColor,
} from './coupon-tag-color'
import { CouponTagPill } from './coupon-tag-pill'

type CouponTagsFieldProps = {
  tags: AffiliateCouponTagSchema[]
  value: string[]
  loading: boolean
  loadError: boolean
  creating: boolean
  createError: boolean
  createServerError: unknown
  deleting: boolean
  deleteError: boolean
  onChange: (tagIds: string[]) => void
  onCreateTag: (
    request: AffiliateCreateCouponTagRequest,
  ) => Promise<AffiliateCouponTagSchema | null>
  onDeleteTag: (tagId: string) => Promise<boolean>
  /** Overrides the default "Tags (optional)" label. The bulk editor sets its
   *  own so the field states that it ADDS to each link's existing tags — the
   *  create/edit dialogs' wording would read as a replace over a selection. */
  label?: string
  helperText?: string
  /**
   * Whether the popup also lets the admin create and delete tags themselves.
   * The bulk editor's "Remove tags" picker turns this off: creating a tag in
   * order to strip it is nonsense, and putting global tag deletion next to
   * "remove from these links" invites confusing one for the other.
   */
  allowManage?: boolean
  /** Distinguishes two pickers rendered in the same dialog. */
  dataTst?: string
}

const CREATE_TAG_DEFAULTS: AffiliateCreateCouponTagRequest = {
  name: '',
  color_hex: DEFAULT_COUPON_TAG_COLOR,
}

export const CouponTagsField = ({
  tags,
  value,
  loading,
  loadError,
  creating,
  createError,
  createServerError,
  deleting,
  deleteError,
  onChange,
  onCreateTag,
  onDeleteTag,
  label,
  helperText,
  allowManage = true,
  dataTst = 'coupon-tags',
}: CouponTagsFieldProps) => {
  const { formatMessage } = useIntl()
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
  } = useForm<AffiliateCreateCouponTagRequest>({
    resolver: zodResolver(couponTagCreateFormSchema),
    defaultValues: CREATE_TAG_DEFAULTS,
    mode: 'onChange',
  })
  const search = useWatch({ control, name: 'name' })
  const colorHex = useWatch({ control, name: 'color_hex' })
  const [deleteTarget, setDeleteTarget] = useState<AffiliateCouponTagSchema | null>(null)
  const [deleteAttempted, setDeleteAttempted] = useState(false)
  const selectedIds = new Set(value)
  const selectedTags = value
    .map((tagId) => tags.find((tag) => tag.id === tagId))
    .filter((tag): tag is AffiliateCouponTagSchema => tag !== undefined)
  const needle = search.trim().toLocaleLowerCase()
  const matchingTags = tags.filter(
    (tag) => needle === '' || tag.name.toLocaleLowerCase().includes(needle),
  )
  const duplicateName = tags.some((tag) => tag.name.toLocaleLowerCase() === needle)
  const showsCreateForm =
    allowManage && needle !== '' && errors.name === undefined && !duplicateName
  const canCreate = showsCreateForm && errors.color_hex === undefined
  const nameError =
    errors.name?.type === 'server' || needle !== ''
      ? fieldErrorText(formatMessage, errors.name, 'coupon.')
      : undefined
  const colorError = fieldErrorText(formatMessage, errors.color_hex, 'coupon.')
  // Every picker in a dialog shares ONE tag state, so a picker that cannot manage
  // tags must not adopt its sibling's create failure or go inert while the
  // sibling saves. Gate the three shared inputs once, here, rather than at each
  // place that reads them — otherwise `allowManage={false}` suppresses the manage
  // UI while still inheriting the manage configuration's failures.
  const manageCreateError = allowManage && createError
  const manageServerError = allowManage ? createServerError : undefined
  const mutating = allowManage && (creating || deleting)
  const serverFieldErrors = extractValidationFieldErrors(manageServerError)
  const mappedServerFields = serverFieldErrors?.filter(
    ({ field }) => field === 'name' || field === 'color_hex',
  )
  const showGenericCreateError =
    manageCreateError &&
    (serverFieldErrors === null || mappedServerFields?.length !== serverFieldErrors.length)

  useEffect(
    function mapCreateTagServerErrors() {
      for (const { field, message } of extractValidationFieldErrors(manageServerError) ?? []) {
        if (field === 'name' || field === 'color_hex') {
          setError(field, { type: 'server', message })
        }
      }
    },
    [manageServerError, setError],
  )

  const toggleTag = useEvent((tagId: string) => {
    onChange(selectedIds.has(tagId) ? value.filter((id) => id !== tagId) : [...value, tagId])
  })

  const submitTag = useEvent(async (request: AffiliateCreateCouponTagRequest) => {
    if (!canCreate || creating) {
      return
    }
    const createdTag = await onCreateTag(request)
    if (createdTag !== null) {
      onChange([...value, createdTag.id])
      reset(CREATE_TAG_DEFAULTS)
    }
  })
  const createTag = handleSubmit(submitTag)

  const deleteTag = useEvent(async () => {
    if (deleteTarget === null || deleting) {
      return
    }

    setDeleteAttempted(true)
    const deleted = await onDeleteTag(deleteTarget.id)
    if (deleted) {
      onChange(value.filter((tagId) => tagId !== deleteTarget.id))
      setDeleteTarget(null)
      setDeleteAttempted(false)
    }
  })

  return (
    <>
      <Menu.Root>
        <FieldShell
          helperText={helperText ?? formatMessage({ id: 'coupon.tags.helper' })}
          label={label ?? formatMessage({ id: 'coupon.tags' })}
          control={
            <Menu.Trigger
              render={
                <button
                  className="flex min-h-8 w-full items-center justify-between gap-2 rounded-sm border border-solid border-transparent bg-components-input-filled-enabledFill px-2 py-1 text-left text-sm font-medium text-text-secondary transition-colors hover:bg-components-input-filled-hoverFill hover:text-text-primary focus-visible:border-primary-main focus-visible:bg-components-input-filled-enabledFill focus-visible:text-text-primary focus-visible:outline-none"
                  data-tst={dataTst}
                  type="button"
                />
              }
            >
              <span className="flex min-w-0 flex-1 flex-wrap gap-1">
                {selectedTags.length === 0 ? (
                  <span className="text-text-secondary">
                    {formatMessage({ id: 'coupon.tags.placeholder' })}
                  </span>
                ) : (
                  selectedTags.map((tag) => (
                    <CouponTagPill
                      key={tag.id}
                      compact
                      tag={tag}
                    />
                  ))
                )}
              </span>
              <ChevronDownIcon className="size-4 shrink-0 text-text-secondary" />
            </Menu.Trigger>
          }
        />
        <Menu.Portal>
          <Menu.Positioner align="start">
            <Menu.Popup
              className="max-h-[var(--available-height)] w-[var(--anchor-width)] min-w-72 gap-2 p-2"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <TextField
                {...register('name')}
                autoFocus
                disabled={mutating}
                error={nameError}
                size="small"
                // A picker that cannot create must not advertise creating.
                aria-label={formatMessage({
                  id: allowManage ? 'coupon.tags.search' : 'coupon.tags.searchOnly',
                })}
                placeholder={formatMessage({
                  id: allowManage
                    ? 'coupon.tags.searchPlaceholder'
                    : 'coupon.tags.searchOnlyPlaceholder',
                })}
                onKeyDown={(event) => {
                  if (event.key.length === 1) {
                    event.stopPropagation()
                  }
                  if (event.key === 'Enter' && canCreate) {
                    event.preventDefault()
                    void createTag()
                  }
                }}
              />

              <Menu.Group className="max-h-44 gap-1 overflow-y-auto p-0">
                {loading ? (
                  <Typography
                    className="px-2 py-1"
                    color="text.secondary"
                    variant="body2"
                  >
                    {formatMessage({ id: 'coupon.tags.loading' })}
                  </Typography>
                ) : loadError ? (
                  <Typography
                    className="px-2 py-1"
                    color="error"
                    variant="body2"
                  >
                    {formatMessage({ id: 'coupon.tags.loadError' })}
                  </Typography>
                ) : matchingTags.length === 0 && !canCreate ? (
                  <Typography
                    className="px-2 py-1"
                    color="text.secondary"
                    variant="body2"
                  >
                    {formatMessage({
                      id: allowManage ? 'coupon.tags.empty' : 'coupon.tags.emptyNoCreate',
                    })}
                  </Typography>
                ) : (
                  matchingTags.map((tag) => {
                    const selected = selectedIds.has(tag.id)

                    return (
                      <div
                        key={tag.id}
                        className="flex min-w-0 items-center gap-1"
                      >
                        <Menu.CheckboxItem
                          checked={selected}
                          className="min-w-0 flex-1"
                          closeOnClick={false}
                          disabled={mutating}
                          onCheckedChange={() => toggleTag(tag.id)}
                        >
                          <CouponTagPill
                            compact
                            tag={tag}
                          />
                        </Menu.CheckboxItem>
                        {allowManage && (
                          <Menu.Item
                            className="w-5 shrink-0 justify-center px-0 text-error-main [&>svg]:size-4 [&[data-highlighted]]:text-error-main"
                            disabled={creating || deleting}
                            aria-label={formatMessage(
                              { id: 'coupon.tags.delete' },
                              { name: tag.name },
                            )}
                            onClick={() => {
                              setDeleteAttempted(false)
                              setDeleteTarget(tag)
                            }}
                          >
                            <TrashIcon />
                          </Menu.Item>
                        )}
                      </div>
                    )
                  })
                )}
              </Menu.Group>

              {showsCreateForm && (
                <div className="border-t border-divider/30 pt-2">
                  <Typography
                    className="mb-2"
                    color="text.secondary"
                    variant="body2"
                  >
                    {formatMessage({ id: 'coupon.tags.create' }, { name: search.trim() })}
                  </Typography>
                  <FieldShell
                    error={colorError}
                    control={
                      <div
                        aria-label={formatMessage({ id: 'coupon.tags.colorLabel' })}
                        className="grid grid-cols-4 gap-1"
                        role="group"
                      >
                        {couponTagColorOptions.map((option) => (
                          <Button
                            key={option.value}
                            aria-pressed={colorHex === option.value}
                            className={cn('h-8 min-w-0', option.className)}
                            disabled={creating}
                            variant="text"
                            aria-label={formatMessage(
                              { id: 'coupon.tags.setColor' },
                              { color: option.value },
                            )}
                            style={
                              option.className === null
                                ? {
                                    backgroundColor: option.value,
                                    color: getTagForegroundColor(option.value),
                                  }
                                : undefined
                            }
                            onClick={() =>
                              setValue('color_hex', option.value, {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                            }
                          >
                            {colorHex === option.value && <CheckIcon className="size-4" />}
                          </Button>
                        ))}
                      </div>
                    }
                  />
                  <Button
                    className="mt-2 w-full"
                    disabled={creating || !canCreate}
                    loading={creating}
                    size="small"
                    variant="contained"
                    onClick={() => void createTag()}
                  >
                    {formatMessage({ id: 'coupon.tags.createButton' })}
                  </Button>
                </div>
              )}
              {showGenericCreateError && needle !== '' && (
                <Typography
                  className="px-2"
                  color="error"
                  variant="body3"
                >
                  {formatMessage({ id: 'coupon.tags.createError' })}
                </Typography>
              )}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>

      {deleteTarget !== null && (
        <ConfirmDialog
          open
          confirmColor="error"
          confirmLabel={formatMessage({ id: 'coupon.tags.deleteConfirm' })}
          loading={deleting}
          title={formatMessage({ id: 'coupon.tags.deleteTitle' })}
          description={formatMessage(
            { id: 'coupon.tags.deleteDescription' },
            { name: deleteTarget.name },
          )}
          onConfirm={() => void deleteTag()}
          onCancel={() => {
            setDeleteAttempted(false)
            setDeleteTarget(null)
          }}
        >
          <MutationError
            messageId="coupon.tags.deleteError"
            show={deleteAttempted && deleteError}
          />
        </ConfirmDialog>
      )}
    </>
  )
}
